import { OwAccordion } from "@components/shared/OwAccordion/OwAccordion";
import OwButton from "@components/shared/OwButton/OwButton";
import OwInputWrapper from "@components/shared/OwInputWrapper/OwInputWrapper";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import OwPill from "@components/shared/OwPill/OwPill";
import OwSelectOption from "@components/shared/OwSelectOption/OwSelectOption";
import WhatsThis from "@components/shared/WhatsThis/WhatsThis";
import { SocketContext } from "@contexts/SocketContext";
import SupplierContext from "@contexts/SupplierContext";
import UiContext from "@contexts/UiContext";
import UserContext from "@contexts/UserContext";
import IOnQBDGenerateWebConnectorConfigDTO from "@models/DTO/IOnQBDGenerateWebConnectorConfigDTO";
import IQBDGenerateWebConnectorConfigDTO from "@models/DTO/IQBDGenerateWebConnectorConfigDTO";
import IUpdateSupplierIntegrationsDTO from "@models/DTO/IUpdateSupplierIntegrationsDTO";
import EndpointsEnum from "@models/EndpointsEnum";
import INotification from "@models/INotification";
import IUiSocketSetup from "@models/IUiSocketSetup";
import NotificationLevelEnum from "@models/NotificationLevelEnum";
import NotificationPositionEnum from "@models/NotificationPositionEnum";
import QBDVersionEnum from "@models/QuickBooks Desktop/QBDVersionEnum";
import TitleTagEnum from "@models/TitleTagEnum";
import FileSaver from "file-saver";
import React, { useContext, useEffect, useState } from "react";

type Props = {};

export const QuickbooksDesktopConfigSetup = (props: Props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);

    const [supplierIntegration, setSupplierIntegration] = useState({
        qbd_version: supplierModel.Supplier_Integration.qbd_version,
        qbd_year: supplierModel.Supplier_Integration.qbd_year,
        qbd_CompanyFilePath: supplierModel.Supplier_Integration.qbd_CompanyFilePath,
        qbd_password: "",
    });

    const [passwordInputType, setPasswordInputType] = useState<"password" | "text">("password");
    const [activeGenerateConfig, setActiveGenerateConfig] = useState(false);
    const [activeSaveDesktopDetails, setActiveSaveDesktopDetails] = useState(false);
    const [activeSavePassword, setActiveSavePassword] = useState(false);
    const [activeSaveCompanyFilePath, setActiveSaveCompanyFilePath] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [hasCompanyFilePathUpdate, setHasCompanyFilePathUpdate] = useState(false);
    const [hasPasswordUpdate, setHasPasswordUpdate] = useState(false);
    const [hasDetailsUpdate, setHasDetailsUpdate] = useState(false);

    const handleChange = (e: React.ChangeEvent<any>) => {
        const value = e.target.value;
        const name = e.target.name;

        const updatedSupplierIntegration = {
            ...supplierIntegration,
            [name]: value,
        };

        if (name === "qbd_password") {
            setHasPasswordUpdate(true);
        }

        if (name === "qbd_CompanyFilePath") {
            setHasCompanyFilePathUpdate(true);
        }

        if (name === "qbd_version" || name === "qbd_year") {
            setHasDetailsUpdate(true);
        }

        setSupplierIntegration(updatedSupplierIntegration);
    };

    const togglePasswordInputMask = (title: string) => {
        let _passwordInputType: "text" | "password" = "text";

        if (passwordInputType === "text") {
            _passwordInputType = "password";
        }

        setPasswordInputType(_passwordInputType);
    };

    const generateWebConnectorConfig = () => {
        const payload: IQBDGenerateWebConnectorConfigDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
        };

        socket.emit(EndpointsEnum.QBDGenerateWebConnectorConfig, payload);

        setActiveGenerateConfig(true);
    };

    const onQbdGenerateWebConnectorConfig = (data: IOnQBDGenerateWebConnectorConfigDTO) => {
        const file = new File([data.data], "orderwerks.qwc", { type: "text/xml;charset=utf-8" });
        FileSaver.saveAs(file);

        setSupplierIntegration({
            ...supplierIntegration,
            qbd_password: data.password,
        });

        setActiveGenerateConfig(false);
    };

    const updateIntegration = () => {
        const payload: IUpdateSupplierIntegrationsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            // @ts-expect-error
            Supplier_Integration: {},
        };

        if (hasCompanyFilePathUpdate) {
            payload.Supplier_Integration.qbd_CompanyFilePath = supplierIntegration.qbd_CompanyFilePath;
        }

        if (hasPasswordUpdate) {
            payload.Supplier_Integration.qbd_password = supplierIntegration.qbd_password;
        }

        if (hasDetailsUpdate) {
            payload.Supplier_Integration.qbd_version = supplierIntegration.qbd_version as QBDVersionEnum;
            payload.Supplier_Integration.qbd_year = supplierIntegration.qbd_year;
        }

        socket.emit(EndpointsEnum.SupplierUpdateSupplierIntegrations, payload);

        setUpdating(true);
    };

    const onUpdateSupplierIntegrations = (data: IUpdateSupplierIntegrationsDTO) => {
        if (updating) {
            const notification: INotification = {
                level: NotificationLevelEnum.success,
                title: "updated_quickbooks_desktop_integration",
                position: NotificationPositionEnum.br,
            };

            uiContext.notifyFn(null, notification);

            setUpdating(false);
        }
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.QBDOnGenerateWebConnectorConfig, fn: onQbdGenerateWebConnectorConfig },
        { endpoint: EndpointsEnum.SupplierOnUpdateSupplierIntegrations, fn: onUpdateSupplierIntegrations },
    ];

    useEffect(() => {
        sockets.forEach((_socket) => {
            socket.on(_socket.endpoint, _socket.fn);
        });

        return () => {
            sockets.forEach((_socket) => {
                socket.off(_socket.endpoint, _socket.fn);
            });
        };
    }, []);

    return (
        <div className={"QuickbooksDesktopConfigSetup"}>
            <OwAccordion title={"setup"} fullWidthClick={true} titleTag={TitleTagEnum.H5}>
                <div className={"mb-5"}>
                    <div>
                        <OwPill title={"step_1"} />
                        <OwLabel title={"quickbooks_desktop_details"} className={"ms-2"} isBold={true} />
                    </div>

                    <div>
                        <OwLabel title={"version"} />
                        <OwSelectOption
                            fn={handleChange}
                            valueProp={"value"}
                            titleProp={"title"}
                            name={"qbd_version"}
                            options={[
                                { title: "Pro", value: QBDVersionEnum.PRO },
                                { title: "Premiere", value: QBDVersionEnum.PREMIERE },
                                { title: "Enterprise", value: QBDVersionEnum.ENTERPRISE },
                            ]}
                            selectedOption={supplierIntegration}
                            selectedOptionProp={"qbd_version"}
                        />
                    </div>

                    <div className={"mt-2"}>
                        <OwLabel title={"year_edition"} />
                        <OwInputWrapper fn={handleChange} name={"qbd_year"} value={supplierIntegration.qbd_year} />
                    </div>

                    <div className={"mt-2"}>
                        <OwButton fn={updateIntegration} title={"save"} active={activeSaveDesktopDetails} disableOnActive={true} />
                    </div>
                </div>

                <div className={"mt-5 mb-5"}>
                    <div>
                        <OwPill title={"step_2"} />
                        <OwLabel title={"web_connector_password"} className={"ms-2"} isBold={true} />{" "}
                        <WhatsThis subject={"qbd_web_connector_password"} />
                    </div>

                    <div>
                        {supplierModel.Supplier_Integration.qbd_password ? (
                            <OwLabel className={"text-success"} title={"has_password_set"} leftIconClass={"fas fa-check"} />
                        ) : (
                            <OwLabel className={"text-danger"} title={"no_password_set"} leftIconClass={"fas fa-times"} />
                        )}
                    </div>

                    <div>
                        <OwInputWrapper
                            placeholder={"password"}
                            fn={handleChange}
                            value={supplierIntegration.qbd_password}
                            name={"qbd_password"}
                            type={passwordInputType}
                        />
                        <OwLabel leftIconClass={"far fa-eye"} title={"toggle_password_mask"} fn={togglePasswordInputMask} />
                    </div>

                    <div>
                        <OwButton fn={updateIntegration} title={"save"} active={activeSavePassword} disableOnActive={true} />
                    </div>
                </div>

                <div>
                    <OwPill title={"step_3"} />
                    <OwLabel title={"company_file_path"} className={"ms-2"} isBold={true} /> <WhatsThis subject={"qbd_company_file_path"} />
                </div>
                <div>
                    <OwInputWrapper fn={handleChange} name={"qbd_CompanyFilePath"} value={supplierIntegration.qbd_CompanyFilePath} />
                </div>
                <div className={"mt-2 mb-5"}>
                    <OwButton fn={updateIntegration} title={"save"} active={activeSaveCompanyFilePath} disableOnActive={true} />
                </div>
                {supplierModel.Supplier_Integration.has_qbd ? (
                    <div className={"col-12"}>
                        <div className={"mb-2"}>
                            <OwPill title={"step_4"} />
                            <WhatsThis subject={"qbd_web_connector_info"} />
                        </div>
                        <OwButton
                            leftIconClass={"fad fa-file-download"}
                            fn={generateWebConnectorConfig}
                            title={"generate_web_connector_config"}
                            active={activeGenerateConfig}
                            disabled={
                                activeGenerateConfig ||
                                !supplierModel.Supplier_Integration.qbd_CompanyFilePath ||
                                !supplierModel.Supplier_Integration.qbd_CompanyFilePath.length
                            }
                        />
                    </div>
                ) : null}
            </OwAccordion>
        </div>
    );
};
